import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../components/ui/collapsible';
import { MonthPicker } from '../../components/ui/date-picker';
import { 
  FileText, Plus, Edit, Trash2, ArrowLeft, 
  BookOpen, CheckCircle, Clock, Filter, Loader2, ChevronDown, ChevronRight, Calendar, Flag, Download, Search, MoreHorizontal, ClipboardPen
} from 'lucide-react';
import { toast } from 'sonner';
import ExportModal from '../../components/ExportModal';
import BulkAnswerControls from '../../components/admin/BulkAnswerControls';
import WrittenSearchModal from '../../components/admin/WrittenSearchModal';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '../../components/ui/dropdown-menu';
import { useAuth } from '../../context/AuthContext';
import useReviewConfig from '../../hooks/useReviewConfig';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const WrittenManagementPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isFullAdmin = user?.role === 'admin';
  // Iter 113: Needs Review workflow
  const { enabled: reviewEnabled, categories: reviewCategories } = useReviewConfig();
  const [reviewPaperMap, setReviewPaperMap] = useState({ paper_ids: [], counts: {}, categories: {} });
  const [reviewCategoryFilter, setReviewCategoryFilter] = useState('all');
  const [papers, setPapers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState({ grades: [], functions: [] });
  
  // Filter states
  const [selectedGrade, setSelectedGrade] = useState('all');
  const [selectedFunction, setSelectedFunction] = useState('all');
  const [selectedYear, setSelectedYear] = useState('all');
  const [selectedMonth, setSelectedMonth] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all'); // all, has_unanswered, fully_answered, empty, no_topic
  
  // Year expansion states - current year expanded by default
  const currentYear = new Date().getFullYear();
  const [expandedYears, setExpandedYears] = useState({ [currentYear]: true });
  
  // Create paper modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({
    grade: '',
    function_id: '',
    exam_month: '',
    paper_number: null,
    total_marks: '',
    source_note: ''
  });
  const [creating, setCreating] = useState(false);
  
  // Edit paper modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingPaper, setEditingPaper] = useState(null);
  const [editForm, setEditForm] = useState({
    exam_month: '',
    paper_number: '',
    total_marks: '',
    source_note: ''
  });
  const [saving, setSaving] = useState(false);

  // Export modal states
  const [showExportModal, setShowExportModal] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportCount, setExportCount] = useState(0);
  const [exportFilters, setExportFilters] = useState({
    grade: 'all',
    function_id: 'all',
    status: 'all'
  });

  // Get available years from papers
  const availableYears = useMemo(() => {
    const yearsMap = {};
    papers.forEach(paper => {
      const match = paper.exam_month?.match(/\d{4}/);
      if (match) {
        const year = match[0];
        if (!yearsMap[year]) yearsMap[year] = 0;
        yearsMap[year]++;
      }
    });
    // Sort years descending
    return Object.entries(yearsMap)
      .sort((a, b) => parseInt(b[0]) - parseInt(a[0]))
      .map(([year, count]) => ({ year, count }));
  }, [papers]);

  // Get available months for selected year
  const availableMonthsForYear = useMemo(() => {
    if (selectedYear === 'all') return [];
    
    const monthsMap = {};
    const monthOrder = ['January', 'February', 'March', 'April', 'May', 'June',
                        'July', 'August', 'September', 'October', 'November', 'December'];
    
    papers.forEach(paper => {
      if (paper.exam_month?.includes(selectedYear)) {
        // Extract month name
        const monthName = monthOrder.find(m => paper.exam_month.includes(m));
        if (monthName) {
          if (!monthsMap[monthName]) monthsMap[monthName] = 0;
          monthsMap[monthName]++;
        }
      }
    });
    
    // Sort months in calendar order (reverse for newest first)
    return Object.entries(monthsMap)
      .sort((a, b) => monthOrder.indexOf(b[0]) - monthOrder.indexOf(a[0]))
      .map(([month, count]) => ({ month, count }));
  }, [papers, selectedYear]);

  // Calculate status counts for pills
  const statusCounts = useMemo(() => {
    let hasUnanswered = 0;
    let fullyAnswered = 0;
    let empty = 0;
    let noTopic = 0;
    let flagged = 0;
    
    papers.forEach(paper => {
      const qCount = paper.question_count || 0;
      const aCount = paper.answered_count || 0;
      const noTopicCount = paper.no_topic_count || 0;
      const flaggedCount = paper.flagged_count || 0;
      
      if (qCount === 0) {
        empty++;
      } else if (aCount < qCount) {
        hasUnanswered++;
      } else {
        fullyAnswered++;
      }
      
      if (noTopicCount > 0) {
        noTopic++;
      }
      
      if (flaggedCount > 0) {
        flagged++;
      }
    });
    
    return { hasUnanswered, fullyAnswered, empty, noTopic, flagged };
  }, [papers]);

  // Filter papers by status, year, and month
  const filteredPapers = useMemo(() => {
    let result = papers;
    
    // Filter by year
    if (selectedYear !== 'all') {
      result = result.filter(p => p.exam_month?.includes(selectedYear));
    }
    
    // Filter by month (within selected year)
    if (selectedMonth !== 'all' && selectedYear !== 'all') {
      result = result.filter(p => p.exam_month?.includes(selectedMonth));
    }
    
    // Filter by status
    if (statusFilter !== 'all') {
      result = result.filter(paper => {
        const qCount = paper.question_count || 0;
        const aCount = paper.answered_count || 0;
        const noTopicCount = paper.no_topic_count || 0;
        const flaggedCount = paper.flagged_count || 0;
        
        switch (statusFilter) {
          case 'has_unanswered':
            return qCount > 0 && aCount < qCount;
          case 'fully_answered':
            return qCount > 0 && aCount === qCount;
          case 'empty':
            return qCount === 0;
          case 'no_topic':
            return noTopicCount > 0;
          case 'flagged':
            return flaggedCount > 0;
          case 'needs_review': {
            if (!reviewPaperMap.paper_ids?.includes(paper.id)) return false;
            if (reviewCategoryFilter === 'uncategorized') {
              // Paper contains flagged questions but at least one has no category
              // The endpoint returns only non-null category ids per paper,
              // so "uncategorized" means the paper has fewer categories than
              // its flagged count. We approximate: keep if the categories
              // array is empty OR shorter than the count.
              const cats = reviewPaperMap.categories?.[paper.id] || [];
              const cnt = reviewPaperMap.counts?.[paper.id] || 0;
              return cats.length < cnt;
            }
            if (reviewCategoryFilter && reviewCategoryFilter !== 'all') {
              return (reviewPaperMap.categories?.[paper.id] || []).includes(reviewCategoryFilter);
            }
            return true;
          }
          default:
            return true;
        }
      });
    }
    
    return result;
  }, [papers, selectedYear, selectedMonth, statusFilter, reviewPaperMap, reviewCategoryFilter]);

  // Group papers by year (use filteredPapers)
  const papersByYear = useMemo(() => {
    const grouped = {};
    filteredPapers.forEach(paper => {
      // Extract year from exam_month (e.g., "December 2024" -> 2024)
      const match = paper.exam_month?.match(/\d{4}/);
      const year = match ? parseInt(match[0]) : currentYear;
      if (!grouped[year]) grouped[year] = [];
      grouped[year].push(paper);
    });
    
    // Sort papers within each year by month (newest first)
    const monthOrder = ['December', 'November', 'October', 'September', 'August', 'July', 
                        'June', 'May', 'April', 'March', 'February', 'January'];
    Object.keys(grouped).forEach(year => {
      grouped[year].sort((a, b) => {
        const monthA = monthOrder.findIndex(m => a.exam_month?.includes(m));
        const monthB = monthOrder.findIndex(m => b.exam_month?.includes(m));
        if (monthA !== monthB) return monthA - monthB;
        // Same month - sort by paper number
        return (a.paper_number || 0) - (b.paper_number || 0);
      });
    });
    
    return grouped;
  }, [filteredPapers, currentYear]);

  // Get sorted years (newest first)
  const sortedYears = useMemo(() => {
    return Object.keys(papersByYear).map(Number).sort((a, b) => b - a);
  }, [papersByYear]);

  // Toggle year expansion
  const toggleYear = (year) => {
    setExpandedYears(prev => ({ ...prev, [year]: !prev[year] }));
  };

  // ── Fetch filter definitions ONCE on mount ─────────────────────────
  // The list of grades and functions doesn't depend on which filter the
  // user has picked — it's the master catalogue. Refetching on every
  // filter click was wasted bandwidth (fix #2 of the "filter feels
  // unresponsive" investigation). Grade→function chip filtering still
  // happens entirely client-side via `filteredFunctions` memo below.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const filtersRes = await axios.get(`${API}/written/filters`);
        if (!cancelled) setFilters(filtersRes.data);
      } catch (error) {
        if (!cancelled) console.error('Failed to fetch filters:', error);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Iter 113: fetch flagged paper ids separately so it re-fires when
  // `reviewEnabled` flips from false → true after the config resolves.
  useEffect(() => {
    if (!reviewEnabled) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/review/written/flagged-paper-ids`);
        if (!cancelled) setReviewPaperMap(r.data || { paper_ids: [], counts: {}, categories: {} });
      } catch (e) { if (process.env.NODE_ENV !== 'production') console.warn('[WrittenMgmt] flagged-paper-ids fetch failed (non-blocking):', e); }
    })();
    return () => { cancelled = true; };
  }, [reviewEnabled]);

  // ── Fetch papers + stats when filters change ────────────────────────
  // AbortController cancels the previous in-flight request before firing
  // a new one, so rapid filter clicks on slow networks never race — the
  // UI always reflects the LAST clicked filter, never a stale response
  // that finished late. Fixes the "sometimes filter is unresponsive" bug.
  useEffect(() => {
    const controller = new AbortController();
    fetchPapersAndStats(controller.signal);
    return () => controller.abort();
  }, [selectedGrade, selectedFunction]);

  const fetchPapersAndStats = async (signal) => {
    try {
      setLoading(true);

      let url = `${API}/written/papers`;
      const params = new URLSearchParams();
      if (selectedGrade !== 'all') params.append('grade', selectedGrade);
      if (selectedFunction !== 'all') params.append('function_id', selectedFunction);
      if (params.toString()) url += `?${params.toString()}`;

      const [papersRes, statsRes] = await Promise.all([
        axios.get(url, { signal }),
        axios.get(`${API}/written/stats`, { signal }),
      ]);

      setPapers(papersRes.data.papers || []);
      setStats(statsRes.data);
      // Iter 113: fetch flagged paper ids (lightweight — one query per load)
      if (reviewEnabled) {
        try {
          const r = await axios.get(`${API}/admin/review/written/flagged-paper-ids`, { signal });
          setReviewPaperMap(r.data || { paper_ids: [], counts: {}, categories: {} });
        } catch (e) { if (process.env.NODE_ENV !== 'production') console.warn('[WrittenMgmt] flagged-paper-ids fetch failed (non-blocking):', e); }
      }
    } catch (error) {
      // Axios throws CanceledError / AbortError when the AbortController
      // fires. That's expected during rapid filter changes — swallow it.
      if (axios.isCancel?.(error) || error?.name === 'CanceledError' || error?.name === 'AbortError') {
        return;
      }
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load written papers');
    } finally {
      // Only clear loading if this request wasn't cancelled — otherwise
      // the *newer* in-flight request is still loading and will clear it.
      if (!signal?.aborted) setLoading(false);
    }
  };

  // Convert "Month Year" format to "YYYY-MM" for MonthPicker
  const monthToPickerFormat = (monthStr) => {
    if (!monthStr) return '';
    const months = ['January', 'February', 'March', 'April', 'May', 'June',
                    'July', 'August', 'September', 'October', 'November', 'December'];
    const parts = monthStr.split(' ');
    if (parts.length !== 2) return '';
    const monthIndex = months.indexOf(parts[0]);
    if (monthIndex === -1) return '';
    const year = parts[1];
    return `${year}-${String(monthIndex + 1).padStart(2, '0')}`;
  };

  // Convert "YYYY-MM" format to "Month Year" for backend
  const pickerToMonthFormat = (pickerValue) => {
    if (!pickerValue) return '';
    const months = ['January', 'February', 'March', 'April', 'May', 'June',
                    'July', 'August', 'September', 'October', 'November', 'December'];
    const [year, month] = pickerValue.split('-');
    const monthIndex = parseInt(month) - 1;
    return `${months[monthIndex]} ${year}`;
  };

  const handleCreatePaper = async () => {
    if (!createForm.grade || !createForm.function_id || !createForm.exam_month) {
      toast.error('Please fill in Grade, Function, and Exam Month');
      return;
    }
    
    try {
      setCreating(true);
      const response = await axios.post(`${API}/written/papers`, {
        ...createForm,
        exam_month: pickerToMonthFormat(createForm.exam_month),
        total_marks: createForm.total_marks ? parseInt(createForm.total_marks) : null,
        paper_number: createForm.paper_number ? parseInt(createForm.paper_number) : null
      });
      
      toast.success('Paper created successfully');
      setShowCreateModal(false);
      setCreateForm({ grade: '', function_id: '', exam_month: '', paper_number: null, total_marks: '', source_note: '' });
      
      // Navigate to paper detail to add questions
      navigate(`/admin/written/${response.data.paper.id}`);
    } catch (error) {
      console.error('Failed to create paper:', error);
      toast.error('Failed to create paper');
    } finally {
      setCreating(false);
    }
  };

  const openEditModal = (paper, e) => {
    if (e) e.stopPropagation();
    console.log('Opening edit modal for paper:', paper.id);
    setEditingPaper(paper);
    setEditForm({
      exam_month: monthToPickerFormat(paper.exam_month),
      paper_number: paper.paper_number?.toString() || '',
      total_marks: paper.total_marks?.toString() || '',
      source_note: paper.source_note || ''
    });
    setShowEditModal(true);
  };

  const handleUpdatePaper = async () => {
    if (!editingPaper) return;
    
    try {
      setSaving(true);
      await axios.put(`${API}/written/papers/${editingPaper.id}`, {
        exam_month: editForm.exam_month ? pickerToMonthFormat(editForm.exam_month) : undefined,
        paper_number: editForm.paper_number ? parseInt(editForm.paper_number) : undefined,
        total_marks: editForm.total_marks ? parseInt(editForm.total_marks) : undefined,
        source_note: editForm.source_note || undefined
      });
      
      toast.success('Paper updated successfully');
      setShowEditModal(false);
      setEditingPaper(null);
      fetchPapersAndStats();
    } catch (error) {
      console.error('Failed to update paper:', error);
      toast.error('Failed to update paper');
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePaper = async (paperId) => {
    // Defense in depth: server blocks sub-admins with 403 via
    // get_current_full_admin (routes/written.py:1060). Suppress the
    // client-side call so we don't fire a request we know will fail.
    if (!isFullAdmin) {
      toast.error('Only full admins can delete papers.');
      return;
    }
    const paper = papers.find(p => p.id === paperId);
    const confirmMsg = paper?.status === 'published' 
      ? '⚠️ This paper is PUBLISHED and visible to users. Are you sure you want to delete it and all its questions?'
      : 'Are you sure you want to delete this paper and all its questions?';
    
    if (!window.confirm(confirmMsg)) return;
    
    try {
      await axios.delete(`${API}/written/papers/${paperId}`);
      toast.success('Paper deleted successfully');
      fetchPapersAndStats();
    } catch (error) {
      console.error('Failed to delete paper:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete paper');
    }
  };

  const handlePublishPaper = async (paperId) => {
    try {
      await axios.put(`${API}/written/papers/${paperId}`, { status: 'published' });
      toast.success('Paper published successfully');
      fetchPapersAndStats();
    } catch (error) {
      console.error('Failed to publish paper:', error);
      toast.error('Failed to publish paper');
    }
  };

  // Export handlers
  const fetchExportCount = async () => {
    try {
      const params = new URLSearchParams();
      if (exportFilters.grade !== 'all') params.append('grade', exportFilters.grade);
      if (exportFilters.function_id !== 'all') params.append('function_id', exportFilters.function_id);
      if (exportFilters.status !== 'all') params.append('status', exportFilters.status);
      
      const response = await axios.get(`${API}/export/written-questions/count?${params}`);
      setExportCount(response.data.count);
    } catch (error) {
      console.error('Failed to fetch export count:', error);
    }
  };

  const handleExport = async (format, activeFilters, selectedColumns) => {
    try {
      setExporting(true);
      
      const params = new URLSearchParams();
      params.append('format', format);
      if (activeFilters.grade) params.append('grade', activeFilters.grade);
      if (activeFilters.function_id) params.append('function_id', activeFilters.function_id);
      if (activeFilters.status) params.append('status', activeFilters.status);
      if (selectedColumns.length > 0) params.append('columns', selectedColumns.join(','));
      
      const response = await axios.get(`${API}/export/written-questions?${params}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `written_questions_export.${format === 'excel' ? 'xlsx' : 'csv'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Export completed successfully');
      setShowExportModal(false);
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  // Fetch export count when export modal opens or filters change
  useEffect(() => {
    if (showExportModal) {
      fetchExportCount();
    }
  }, [showExportModal, exportFilters]);

  // Global Cmd/Ctrl-K hotkey to open the question search modal.
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setShowSearchModal(true);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Filter functions by selected grade
  const filteredFunctions = filters.functions.filter(
    f => selectedGrade === 'all' || f.grade === selectedGrade
  );

  // Get paper display name
  const getPaperDisplayName = (paper) => {
    if (paper.paper_number) {
      return `${paper.exam_month} - Paper ${paper.paper_number}`;
    }
    return paper.exam_month;
  };

  if (loading && papers.length === 0) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-[#0A7EA4]" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page-transition">
        <Button
          variant="ghost"
          onClick={() => navigate('/admin/content')}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Content Management
        </Button>
        
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Written Management</h1>
            <p className="text-gray-600">Manage written exam papers and questions</p>
          </div>
          <div className="flex flex-nowrap items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowSearchModal(true)}
              className="border-[#0A7EA4] text-[#0A7EA4] hover:bg-[#0A7EA4]/5"
              data-testid="written-search-button"
              title="Search across all written questions (Ctrl/⌘ + K)"
            >
              <Search className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Search</span>
              <kbd className="hidden lg:inline ml-2 px-1.5 py-0.5 text-[10px] bg-gray-100 border border-gray-200 rounded font-mono">
                ⌘K
              </kbd>
            </Button>

            {/* Condensed "More actions" — keeps the header to a single row.
                Houses the lower-frequency Bulk Answer + Export commands. */}
            {isFullAdmin && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-gray-300 text-gray-700 hover:bg-gray-50"
                    data-testid="written-more-actions-button"
                  >
                    <MoreHorizontal className="w-4 h-4 sm:mr-2" />
                    <span className="hidden sm:inline">More</span>
                    <ChevronDown className="w-3 h-3 ml-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  className="w-56"
                  data-testid="written-more-actions-menu"
                >
                  <DropdownMenuLabel className="text-xs uppercase tracking-wide text-gray-500">
                    Bulk operations
                  </DropdownMenuLabel>
                  <BulkAnswerControls
                    questionType="written"
                    onCommitted={() => fetchPapersAndStats()}
                    compact
                  />
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel className="text-xs uppercase tracking-wide text-gray-500">
                    Reports
                  </DropdownMenuLabel>
                  <DropdownMenuItem
                    onSelect={(e) => { e.preventDefault(); setShowExportModal(true); }}
                    className="text-green-700 focus:bg-green-50"
                    data-testid="written-export-menu"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Export Papers
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            <Button
              size="sm"
              onClick={() => setShowCreateModal(true)}
              className="bg-[#0A7EA4] hover:bg-[#0A7EA4]/90 text-white"
              data-testid="written-add-paper-button"
            >
              <Plus className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Add New Paper</span>
              <span className="sm:hidden">Add</span>
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-[#0A7EA4]">{stats.total_papers}</p>
                <p className="text-sm text-gray-600">Total Papers</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-amber-600">{stats.draft_papers}</p>
                <p className="text-sm text-gray-600">Draft</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-green-600">{stats.published_papers}</p>
                <p className="text-sm text-gray-600">Published</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-purple-600">{stats.total_questions}</p>
                <p className="text-sm text-gray-600">Total Questions</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters - Button Style */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <Filter className="w-4 h-4 text-gray-500" />
              <span className="font-medium text-gray-700">Filters</span>
            </div>
            
            {/* Grade Filter Buttons */}
            <div className="mb-4">
              <Label className="text-sm text-gray-600 mb-2 block">Grade</Label>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant={selectedGrade === 'all' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => {
                    setSelectedGrade('all');
                    setSelectedFunction('all');
                  }}
                  className={selectedGrade === 'all' ? 'bg-[#0A7EA4]' : ''}
                >
                  All Grades
                </Button>
                {filters.grades.map(grade => (
                  <Button
                    key={grade}
                    variant={selectedGrade === grade ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => {
                      setSelectedGrade(grade);
                      setSelectedFunction('all');
                    }}
                    className={selectedGrade === grade ? 'bg-[#0A7EA4]' : ''}
                  >
                    {grade}
                  </Button>
                ))}
              </div>
            </div>
            
            {/* Function Filter Buttons */}
            <div className="mb-4">
              <Label className="text-sm text-gray-600 mb-2 block">Function</Label>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant={selectedFunction === 'all' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedFunction('all')}
                  className={selectedFunction === 'all' ? 'bg-[#14B8A6]' : ''}
                >
                  All Functions
                </Button>
                {filteredFunctions.map(func => (
                  <Button
                    key={func.id}
                    variant={selectedFunction === func.id ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSelectedFunction(func.id)}
                    className={selectedFunction === func.id ? 'bg-[#14B8A6]' : ''}
                  >
                    {func.display}
                  </Button>
                ))}
              </div>
            </div>
            
            {/* Year and Month Filter Dropdowns */}
            <div className="mb-4">
              <Label className="text-sm text-gray-600 mb-2 block">Period</Label>
              <div className="flex flex-wrap gap-3">
                {/* Year Dropdown */}
                <Select 
                  value={selectedYear} 
                  onValueChange={(value) => {
                    setSelectedYear(value);
                    setSelectedMonth('all'); // Reset month when year changes
                  }}
                >
                  <SelectTrigger className="w-[160px]">
                    <SelectValue placeholder="All Years" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Years</SelectItem>
                    {availableYears.map(({ year, count }) => (
                      <SelectItem key={year} value={year}>
                        {year} ({count} {count === 1 ? 'paper' : 'papers'})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                {/* Month Dropdown - Only enabled when year is selected */}
                <Select 
                  value={selectedMonth} 
                  onValueChange={setSelectedMonth}
                  disabled={selectedYear === 'all'}
                >
                  <SelectTrigger className={`w-[180px] ${selectedYear === 'all' ? 'opacity-50' : ''}`}>
                    <SelectValue placeholder={selectedYear === 'all' ? 'Select year first' : 'All Months'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Months</SelectItem>
                    {availableMonthsForYear.map(({ month, count }) => (
                      <SelectItem key={month} value={month}>
                        {month} ({count})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Status Filter Pills */}
            <div>
              <Label className="text-sm text-gray-600 mb-2 block">Status</Label>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant={statusFilter === 'all' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setStatusFilter('all')}
                  className={statusFilter === 'all' ? 'bg-gray-700' : ''}
                >
                  All ({papers.length})
                </Button>
                <Button
                  variant={statusFilter === 'flagged' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setStatusFilter('flagged')}
                  className={statusFilter === 'flagged' ? 'bg-red-500 hover:bg-red-600' : 'text-red-600 border-red-300'}
                >
                  <Flag className="w-3.5 h-3.5 mr-1" />
                  Flagged ({statusCounts.flagged})
                </Button>
                <Button
                  variant={statusFilter === 'has_unanswered' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setStatusFilter('has_unanswered')}
                  className={statusFilter === 'has_unanswered' ? 'bg-amber-500 hover:bg-amber-600' : 'text-amber-600 border-amber-300'}
                >
                  Has Unanswered ({statusCounts.hasUnanswered})
                </Button>
                <Button
                  variant={statusFilter === 'fully_answered' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setStatusFilter('fully_answered')}
                  className={statusFilter === 'fully_answered' ? 'bg-green-500 hover:bg-green-600' : 'text-green-600 border-green-300'}
                >
                  Fully Answered ({statusCounts.fullyAnswered})
                </Button>
                <Button
                  variant={statusFilter === 'empty' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setStatusFilter('empty')}
                  className={statusFilter === 'empty' ? 'bg-gray-500 hover:bg-gray-600' : 'text-gray-600 border-gray-300'}
                >
                  Empty ({statusCounts.empty})
                </Button>
                <Button
                  variant={statusFilter === 'no_topic' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setStatusFilter('no_topic')}
                  className={statusFilter === 'no_topic' ? 'bg-purple-500 hover:bg-purple-600' : 'text-purple-600 border-purple-300'}
                >
                  No Topic ({statusCounts.noTopic})
                </Button>
                {reviewEnabled && (
                  <Button
                    variant={statusFilter === 'needs_review' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setStatusFilter('needs_review')}
                    className={statusFilter === 'needs_review' ? 'bg-amber-500 hover:bg-amber-600 text-white' : 'text-amber-700 border-amber-300'}
                    data-testid="paper-list-needs-review-btn"
                  >
                    <ClipboardPen className="w-3.5 h-3.5 mr-1" />
                    Needs Review ({reviewPaperMap.paper_ids?.length || 0})
                  </Button>
                )}
              </div>
              {reviewEnabled && statusFilter === 'needs_review' && reviewCategories.length > 0 && (
                <div className="flex items-center gap-2 flex-wrap mt-2" data-testid="paper-list-review-category-filter">
                  <span className="text-xs text-gray-500">Category:</span>
                  {[{ id: 'all', name: 'All' }, { id: 'uncategorized', name: 'Uncategorized' }, ...reviewCategories].map((c) => (
                    <button
                      key={c.id}
                      onClick={() => setReviewCategoryFilter(c.id)}
                      className={
                        'inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-all ' +
                        (reviewCategoryFilter === c.id
                          ? 'bg-amber-600 text-white'
                          : 'bg-amber-50 text-amber-800 border border-amber-200 hover:bg-amber-100')
                      }
                      data-testid={`paper-list-review-cat-${c.id}`}
                    >
                      {c.color && <span className="w-2 h-2 rounded-full" style={{ background: c.color }} />}
                      {c.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Papers List - Year-wise Collapsible */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Written Papers ({filteredPapers.length}{filteredPapers.length !== papers.length ? ` of ${papers.length}` : ''})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {filteredPapers.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No written papers found</p>
                <p className="text-sm mt-1">
                  {selectedGrade !== 'all' || selectedFunction !== 'all' || selectedYear !== 'all' || selectedMonth !== 'all' || statusFilter !== 'all'
                    ? 'Try adjusting filters or ' 
                    : ''}
                  Click &quot;Add New Paper&quot; to create one
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {sortedYears.map(year => (
                  <Collapsible 
                    key={year} 
                    open={expandedYears[year]} 
                    onOpenChange={() => toggleYear(year)}
                  >
                    <CollapsibleTrigger className="w-full">
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                        <div className="flex items-center gap-3">
                          {expandedYears[year] ? (
                            <ChevronDown className="w-5 h-5 text-gray-500" />
                          ) : (
                            <ChevronRight className="w-5 h-5 text-gray-500" />
                          )}
                          <Calendar className="w-5 h-5 text-[#0A7EA4]" />
                          <span className="font-semibold text-lg">{year}</span>
                          <Badge variant="outline" className="ml-2">
                            {papersByYear[year].length} {papersByYear[year].length === 1 ? 'paper' : 'papers'}
                          </Badge>
                        </div>
                      </div>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4 px-1">
                        {papersByYear[year].map(paper => (
                          <div
                            key={paper.id}
                            onClick={() => navigate(`/admin/written/${paper.id}`)}
                            className="p-4 border rounded-lg cursor-pointer transition-all hover:shadow-md hover:border-[#0A7EA4] bg-white"
                          >
                            {/* Status Badge - Top Right */}
                            <div className="flex justify-between items-start mb-2">
                              <Badge className="bg-[#0A7EA4] hover:bg-[#0A7EA4]">{paper.grade}</Badge>
                              <Badge 
                                variant="outline" 
                                className={`text-xs ${paper.status === 'published' 
                                  ? 'border-green-500 text-green-600 hover:bg-transparent' 
                                  : 'border-amber-500 text-amber-600 hover:bg-transparent'}`}
                              >
                                {paper.status === 'published' ? (
                                  <><CheckCircle className="w-3 h-3 mr-1" /> Published</>
                                ) : (
                                  <><Clock className="w-3 h-3 mr-1" /> Draft</>
                                )}
                              </Badge>
                            </div>
                            
                            {/* Paper Title */}
                            <h3 className="font-semibold text-gray-900 mb-1">
                              {getPaperDisplayName(paper)}
                            </h3>
                            
                            {/* Function */}
                            <p className="text-sm text-gray-600 mb-2">{paper.function_name}</p>
                            
                            {/* Question Count */}
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2 flex-wrap">
                                <Badge 
                                  className={`text-xs hover:bg-opacity-100 ${
                                    paper.answered_count === paper.question_count && paper.question_count > 0
                                      ? 'bg-green-500 text-white hover:bg-green-500'
                                      : paper.answered_count > 0
                                        ? 'bg-amber-500 text-white hover:bg-amber-500'
                                        : 'bg-gray-200 text-gray-700 hover:bg-gray-200'
                                  }`}
                                >
                                  {paper.answered_count || 0}/{paper.question_count} answered
                                </Badge>
                                {/* Flagged count - only show if > 0 */}
                                {paper.flagged_count > 0 && (
                                  <Badge className="text-xs bg-red-100 text-red-700 hover:bg-red-100">
                                    <Flag className="w-3 h-3 mr-1" />
                                    {paper.flagged_count} flagged
                                  </Badge>
                                )}
                                {/* No Topic count - only show if > 0 */}
                                {paper.no_topic_count > 0 && (
                                  <Badge className="text-xs bg-purple-100 text-purple-700 hover:bg-purple-100">
                                    {paper.no_topic_count} no topic
                                  </Badge>
                                )}
                                {/* Iter 113: Needs Review pill on paper card */}
                                {reviewEnabled && reviewPaperMap.counts?.[paper.id] > 0 && (
                                  <Badge
                                    className="text-xs bg-amber-100 text-amber-800 hover:bg-amber-100"
                                    data-testid={`paper-review-pill-${paper.id}`}
                                  >
                                    <ClipboardPen className="w-3 h-3 mr-1" />
                                    {reviewPaperMap.counts[paper.id]} needs review
                                  </Badge>
                                )}
                              </div>
                              
                              {/* Action buttons */}
                              <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => openEditModal(paper, e)}
                                  className="h-8 w-8 p-0 text-[#0A7EA4] hover:bg-[#0A7EA4]/10"
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                {/* Delete button — full admins only (backend blocks sub-admins with 403). */}
                                {isFullAdmin && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeletePaper(paper.id);
                                    }}
                                    className="h-8 w-8 p-0 text-red-600 hover:bg-red-50"
                                    data-testid={`delete-paper-${paper.id}`}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create Paper Modal */}
        <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
          <DialogContent className="max-w-lg w-[calc(100vw-2rem)]">
            <DialogHeader>
              <DialogTitle>Create New Paper</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label>Grade *</Label>
                <Select 
                  value={createForm.grade} 
                  onValueChange={(value) => {
                    setCreateForm({ ...createForm, grade: value, function_id: '' });
                  }}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select grade" />
                  </SelectTrigger>
                  <SelectContent>
                    {filters.grades.map(grade => (
                      <SelectItem key={grade} value={grade}>{grade}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Function *</Label>
                <Select 
                  value={createForm.function_id} 
                  onValueChange={(value) => setCreateForm({ ...createForm, function_id: value })}
                  disabled={!createForm.grade}
                >
                  <SelectTrigger className="mt-1 [&>span]:truncate [&>span]:block [&>span]:text-left">
                    <SelectValue placeholder={createForm.grade ? "Select function" : "Select grade first"} />
                  </SelectTrigger>
                  <SelectContent className="max-w-[calc(100vw-4rem)]">
                    {filters.functions
                      .filter(f => f.grade === createForm.grade)
                      .map(func => (
                        <SelectItem key={func.id} value={func.id}>
                          <span className="block truncate max-w-[420px]" title={func.display}>{func.display}</span>
                        </SelectItem>
                      ))
                    }
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Exam Month *</Label>
                <MonthPicker
                  value={createForm.exam_month}
                  onChange={(value) => setCreateForm({ ...createForm, exam_month: value })}
                  placeholder="Select month"
                  fromYear={2000}
                  toYear={new Date().getFullYear()}
                  maxMonth={`${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`}
                  className="mt-1"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Paper Number (Optional)</Label>
                  <Input
                    type="number"
                    min="1"
                    placeholder="e.g., 1, 2"
                    value={createForm.paper_number || ''}
                    onChange={(e) => setCreateForm({ ...createForm, paper_number: e.target.value })}
                    className="mt-1"
                  />
                  <p className="text-xs text-gray-500 mt-1">Only needed if multiple papers for same month</p>
                </div>
                <div>
                  <Label>Total Marks</Label>
                  <Input
                    type="number"
                    min="0"
                    placeholder="e.g., 100"
                    value={createForm.total_marks}
                    onChange={(e) => setCreateForm({ ...createForm, total_marks: e.target.value })}
                    className="mt-1"
                  />
                </div>
              </div>
              
              <div>
                <Label>Source Note (Optional)</Label>
                <Input
                  placeholder="e.g., From Notion submission"
                  value={createForm.source_note}
                  onChange={(e) => setCreateForm({ ...createForm, source_note: e.target.value })}
                  className="mt-1"
                />
              </div>
              
              <div className="flex gap-3 pt-4">
                <Button 
                  variant="outline" 
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleCreatePaper}
                  disabled={creating}
                  className="flex-1 bg-[#14B8A6] hover:bg-[#14B8A6]/90"
                >
                  {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Create & Add Questions
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit Paper Modal */}
        <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
          <DialogContent className="max-w-lg w-[calc(100vw-2rem)]">
            <DialogHeader>
              <DialogTitle>Edit Paper</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              {editingPaper && (
                <div className="p-3 bg-gray-50 rounded-lg mb-4">
                  <p className="text-sm text-gray-600 flex flex-wrap items-center gap-2">
                    <Badge className="bg-[#0A7EA4] flex-shrink-0">{editingPaper.grade}</Badge>
                    <Badge
                      variant="outline"
                      className="max-w-full truncate"
                      title={editingPaper.function_display}
                    >
                      {editingPaper.function_display}
                    </Badge>
                  </p>
                </div>
              )}
              
              <div>
                <Label>Exam Month</Label>
                <MonthPicker
                  value={editForm.exam_month}
                  onChange={(value) => setEditForm({ ...editForm, exam_month: value })}
                  placeholder="Select month"
                  fromYear={2000}
                  toYear={new Date().getFullYear()}
                  maxMonth={`${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`}
                  className="mt-1"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Paper Number (Optional)</Label>
                  <Input
                    type="number"
                    min="1"
                    placeholder="e.g., 1, 2"
                    value={editForm.paper_number}
                    onChange={(e) => setEditForm({ ...editForm, paper_number: e.target.value })}
                    className="mt-1"
                  />
                  <p className="text-xs text-gray-500 mt-1">Only needed if multiple papers for same month</p>
                </div>
                <div>
                  <Label>Total Marks</Label>
                  <Input
                    type="number"
                    min="0"
                    placeholder="e.g., 100"
                    value={editForm.total_marks}
                    onChange={(e) => setEditForm({ ...editForm, total_marks: e.target.value })}
                    className="mt-1"
                  />
                </div>
              </div>
              
              <div>
                <Label>Source Note</Label>
                <Input
                  placeholder="e.g., From Notion submission"
                  value={editForm.source_note}
                  onChange={(e) => setEditForm({ ...editForm, source_note: e.target.value })}
                  className="mt-1"
                />
              </div>
              
              <div className="flex gap-3 pt-4">
                <Button 
                  variant="outline" 
                  onClick={() => setShowEditModal(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleUpdatePaper}
                  disabled={saving}
                  className="flex-1 bg-[#0A7EA4] hover:bg-[#0A7EA4]/90"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Save Changes
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Export Modal */}
        <ExportModal
          open={showExportModal}
          onOpenChange={setShowExportModal}
          title="Export Written Questions"
          description="Export written questions to CSV or Excel format"
          filters={[
            {
              key: 'grade',
              label: 'Grade',
              value: exportFilters.grade,
              onChange: (value) => setExportFilters(prev => ({ ...prev, grade: value })),
              options: filters.grades?.map(g => ({ value: g, label: g })) || []
            },
            {
              key: 'function_id',
              label: 'Function',
              value: exportFilters.function_id,
              onChange: (value) => setExportFilters(prev => ({ ...prev, function_id: value })),
              options: filters.functions?.map(f => ({ value: f.id, label: f.name })) || []
            },
            {
              key: 'status',
              label: 'Answer Status',
              value: exportFilters.status,
              onChange: (value) => setExportFilters(prev => ({ ...prev, status: value })),
              options: [
                { value: 'answered', label: 'Answered' },
                { value: 'unanswered', label: 'Unanswered' }
              ]
            }
          ]}
          columns={[
            { key: 'question_text', label: 'Question' },
            { key: 'answer_text', label: 'Answer' },
            { key: 'marks', label: 'Marks' },
            { key: 'grade', label: 'Grade' },
            { key: 'function_name', label: 'Function' },
            { key: 'paper_exam_month', label: 'Exam Month' },
            { key: 'paper_number', label: 'Paper #' },
            { key: 'topic_name', label: 'Topic' },
            { key: 'question_number', label: 'Q#' },
            { key: 'status', label: 'Status' },
            { key: 'question_image_1', label: 'Question Image 1' },
            { key: 'question_image_2', label: 'Question Image 2' },
            { key: 'question_image_3', label: 'Question Image 3' },
            { key: 'question_image_4', label: 'Question Image 4' },
            { key: 'answer_image_1', label: 'Answer Image 1' },
            { key: 'answer_image_2', label: 'Answer Image 2' },
            { key: 'answer_image_3', label: 'Answer Image 3' },
            { key: 'answer_image_4', label: 'Answer Image 4' },
            { key: 'additional_answer_images', label: 'Additional Answer Images' },
            { key: 'created_at', label: 'Created At' }
          ]}
          onExport={handleExport}
          exporting={exporting}
          totalCount={exportCount}
        />

        {/* Cross-paper question search modal (Cmd/Ctrl-K) */}
        <WrittenSearchModal
          open={showSearchModal}
          onClose={() => setShowSearchModal(false)}
        />
      </div>
    </Layout>
  );
};

export default WrittenManagementPage;
